import Head from "next/head";
import { FormEvent, useState } from "react";
import { ZodError, z } from "zod";
import { formatError } from "@/lib/utils";
import InputErrorMessage from "@/components/InputErrorMessage";
import { useSupabaseClient } from "@supabase/auth-helpers-react";
import Layout from "@/components/Layout";
import {
  Alert,
  Button,
  Center,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ForgotPasswordSchema } from "@/lib/schemas/authValidationSchema";

type FormData = z.infer<typeof ForgotPasswordSchema>;

export default function ForgotPassword() {
  const supabase = useSupabaseClient();
  const [errors, setErrors] = useState<FormData>();
  const [message, setMessage] = useState("");
  const [formSuccess, setFormSuccess] = useState(false);
  const [formData, setFormData] = useState<FormData>({ email: "" });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setFormSuccess(false);
    setErrors(undefined);
    setMessage("");

    const email = formData.email;

    try {
      ForgotPasswordSchema.parse({ email });
    } catch (err) {
      if (err instanceof ZodError) {
        const errs = formatError(err) as FormData;
        setErrors(errs);
        return;
      }
    }

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${new URL(location.href).origin}/auth/update-password`,
    });

    if (error) {
      setMessage(error.message);
      return;
    }

    // reset form
    setFormData({ email: "" });
    setFormSuccess(true);
    setMessage(
      "Please check your email for a password reset link to log into the website.",
    );
  };

  return (
    <>
      <Head>
        <title>Reset Flow</title>
      </Head>

      <Layout>
        <Center gridArea="content" h="full">
          <VStack
            bg="white"
            rounded="md"
            textColor="black"
            p={8}
            w="md"
            spacing={4}>
            <Heading size="md">Forgot Password</Heading>
            <Text>Looks like you&apos;ve forgotten your password</Text>
            <VStack
              as="form"
              w="full"
              spacing={8}
              fontSize="md"
              onSubmit={e =>
                handleSubmit(e as unknown as FormEvent<HTMLFormElement>)
              }>
              <FormControl>
                <FormLabel htmlFor="email">Email</FormLabel>
                <Input
                  id="email"
                  name="email"
                  type="text"
                  value={formData?.email ?? ""}
                  onChange={ev =>
                    setFormData({ ...formData, email: ev.target.value })
                  }
                />
              </FormControl>

              {errors?.email ? (
                <InputErrorMessage>{errors?.email}</InputErrorMessage>
              ) : null}

              {message ? (
                <Alert status={formSuccess ? "success" : "error"}>
                  {message}
                </Alert>
              ) : null}

              <FormControl>
                <Button w="full" type="submit">
                  Send
                </Button>
              </FormControl>
            </VStack>
          </VStack>
        </Center>
      </Layout>
    </>
  );
}
